import { useEffect, useMemo, useState } from 'react';
import { Circle, MapContainer, Marker, Polygon, Polyline, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '@greenrinse/ui';

// Vite doesn't bundle Leaflet's default marker images; point to the CDN copies (pinned version).
const icon = L.icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

export interface LatLng { lat: number; lng: number }

export interface GeofenceValue { location: LatLng; radiusMeters: number; polygon: LatLng[] | null }

/**
 * Site geofence editor:
 *  - drag the marker (or click the map) to set the centre,
 *  - radius slider for a circular fence,
 *  - "Draw polygon" mode: click to add vertices; the polygon takes precedence over the radius when saved.
 */
export function GeofenceEditor({ value, onChange }: { value: GeofenceValue; onChange: (v: GeofenceValue) => void }) {
  const [drawing, setDrawing] = useState(false);
  const center = useMemo<[number, number]>(() => [value.location.lat, value.location.lng], [value.location.lat, value.location.lng]);
  const poly = value.polygon ?? [];

  return (
    <div className="gr-stack" style={{ gap: 8 }}>
      <div className="gr-row" style={{ justifyContent: 'space-between' }}>
        <span className="gr-muted" style={{ fontSize: 14 }}>
          {drawing ? `Click the map to add vertices (${poly.length}). ` : 'Drag the pin or click the map to set the centre. '}
          Lat {value.location.lat.toFixed(5)}, Lng {value.location.lng.toFixed(5)}
        </span>
        <div className="gr-row">
          <Button type="button" variant={drawing ? 'accent' : 'tonal'} style={{ minHeight: 36 }} onClick={() => setDrawing((d) => !d)}>
            {drawing ? 'Finish polygon' : 'Draw polygon'}
          </Button>
          {poly.length > 0 && <Button type="button" variant="outline" style={{ minHeight: 36 }} onClick={() => { onChange({ ...value, polygon: null }); setDrawing(false); }}>Clear polygon</Button>}
        </div>
      </div>
      <div style={{ height: 360, borderRadius: 16, overflow: 'hidden', border: '1px solid var(--gr-border)' }}>
        <MapContainer center={center} zoom={16} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Recenter center={center} />
          <ClickHandler onClick={(ll) => {
            if (drawing) onChange({ ...value, polygon: [...poly, ll] });
            else onChange({ ...value, location: ll });
          }} />
          <Marker position={center} icon={icon} draggable eventHandlers={{ dragend: (e) => { const ll = (e.target as L.Marker).getLatLng(); onChange({ ...value, location: { lat: ll.lat, lng: ll.lng } }); } }} />
          {poly.length === 0 && <Circle center={center} radius={value.radiusMeters} pathOptions={{ color: '#2C9A8E', fillColor: '#A6E3E0', fillOpacity: 0.3 }} />}
          {poly.length >= 3 && <Polygon positions={poly.map((p) => [p.lat, p.lng] as [number, number])} pathOptions={{ color: '#2F6B4F', fillColor: '#B4F1CC', fillOpacity: 0.35 }} />}
          {poly.length > 0 && poly.length < 3 && <Polyline positions={poly.map((p) => [p.lat, p.lng] as [number, number])} pathOptions={{ color: '#2F6B4F' }} />}
        </MapContainer>
      </div>
      {poly.length === 0 && (
        <label className="gr-row" style={{ gap: 12 }}>
          <span style={{ minWidth: 120 }}>Radius: <strong>{value.radiusMeters} m</strong></span>
          <input type="range" min={20} max={2000} step={10} value={value.radiusMeters} onChange={(e) => onChange({ ...value, radiusMeters: Number(e.target.value) })} style={{ flex: 1 }} />
        </label>
      )}
    </div>
  );
}

function ClickHandler({ onClick }: { onClick: (ll: LatLng) => void }) {
  useMapEvents({ click: (e) => onClick({ lat: e.latlng.lat, lng: e.latlng.lng }) });
  return null;
}

function Recenter({ center }: { center: [number, number] }) {
  const map = useMap();
  const [last, setLast] = useState(center);
  useEffect(() => {
    // Only pan when the centre moved a lot (e.g. address lookup / partner switch), not on every drag.
    if (Math.abs(last[0] - center[0]) > 0.01 || Math.abs(last[1] - center[1]) > 0.01) { map.setView(center); setLast(center); }
  }, [center, last, map]);
  return null;
}
